import React, { useState, useEffect, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area, LabelList,
    PieChart, Pie, Cell
} from 'recharts';
import {
    getFirestore, collection, query, where, getDocs, orderBy, Timestamp
} from 'firebase/firestore';
import { Form, Row, Col, Spinner, Button, Offcanvas } from 'react-bootstrap';
import { FaClock, FaCalendarAlt, FaShieldAlt, FaBan, FaUsers, FaDoorOpen, FaQrcode, FaExchangeAlt, FaAward } from 'react-icons/fa';

const formatLocalDate = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
};

const AccessReports = (props) => {
    const { name, filex, apc } = props;
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const todayStr = formatLocalDate();
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);

    const db = getFirestore();
    
    // APC Color Palette
    const COLORS = ['#409da0cb', '#d1a054c9', '#0dcaf0cb', '#a81d32cb', '#6c757dcb'];

    const handleClose = () => { if (props.onToggle) props.onToggle(); };
    const handleShow = () => { if (props.onToggle) props.onToggle(); };

    const fetchReportData = async () => {
        setLoading(true);
        try {
            const start = parseLocalDate(startDate);
            const endExclusive = parseLocalDate(endDate);
            endExclusive.setDate(endExclusive.getDate() + 1);

            const selectedYear = start.getFullYear();
            const logsRef = collection(db, `access_logs_${selectedYear}`);

            const q = query(
                logsRef,
                where("lastScannedAt", ">=", Timestamp.fromDate(start)),
                where("lastScannedAt", "<", Timestamp.fromDate(endExclusive)),
                orderBy("lastScannedAt", "desc")
            );

            const querySnapshot = await getDocs(q);
            const fetchedLogs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLogs(fetchedLogs);
        } catch (err) {
            console.error("Report Fetch Error:", err);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.isOpen) fetchReportData();
    }, [props.isOpen, startDate, endDate]);

    const reportData = useMemo(() => {
        const monthlyMap = {};
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayOfWeekMap = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };
        const sponsorLeaderboardMap = {};

        const hourlyMap = Array.from({ length: 24 }, (_, i) => ({
            hour: i,
            label: i >= 12 ? `${i === 12 ? 12 : i - 12} PM` : `${i === 0 ? 12 : i} AM`,
            Members: 0,
            Guests: 0,
            Bypassed: 0
        }));

        const methodCounts = { qr: 0, manual: 0, eventList: 0, self: 0, quickAdd: 0 };
        
        const stats = { 
            totalScans: 0, totalUniquePatrons: 0, members: 0, guests: 0, 
            unverifiedMembers: 0, unverifiedGuests: 0,
            denied: 0, bypassed: 0, bypassMembers: 0, bypassGuests: 0,
            deniedExpired: 0, deniedFlagged: 0, deniedIncident: 0, deniedBanned: 0,
            estimatedInClub: 0, totalCheckedOut: 0,
            reentryRate: "0.00", peakHourLabel: '--', peakHourVolume: 0
        };

        const uniquePatronPresenceMap = new Map();
        const uniqueMemberIds = new Set();
        const uniqueGuestIds = new Set();
        const uniqueUnverifiedMemberIds = new Set();
        const uniqueUnverifiedGuestIds = new Set();

        logs.forEach(log => {
            if (log.status === 'cancelled' || log.status === 'removed' || log.remove || log.noshow) return;

            // 🟢 FIX: Ensure guest records have their own unique patron ID rather than using sponsor rawPayload
            const patronId = log.entryType === 'Guest'
                ? (log.fullName ? `guest_${log.fullName.toLowerCase().trim().replace(/\s+/g, '_')}` : log.id.toLowerCase())
                : (log.rawPayload || log.id).toLowerCase();

            const isLatestDenied = log.isExpired || log.isFlagged || log.hasIncident || log.isBanned || log.status === 'denied' || log.status === 'blocked';
            const isLatestBypass = log.status === 'bypassed';
            const isLatestVerified = log.isVerified === true;

            // Track Sponsor Guest Leaderboard
            if (log.entryType === 'Guest' && log.sponsorName) {
                const sponsor = log.sponsorName.trim();
                sponsorLeaderboardMap[sponsor] = (sponsorLeaderboardMap[sponsor] || 0) + 1;
            }

            // DEDUPLICATED HEADCOUNT & OCCUPANCY
            if (log.entryType === 'Guest') {
                if (!log.isBanned && log.status !== 'blocked') {
                    uniqueGuestIds.add(patronId);
                    if (!isLatestVerified && !isLatestBypass) {
                        uniqueUnverifiedGuestIds.add(patronId);
                    }
                }
            } else {
                if (!isLatestDenied || isLatestBypass) {
                    uniqueMemberIds.add(patronId);
                    if (!isLatestVerified && !isLatestBypass) {
                        uniqueUnverifiedMemberIds.add(patronId);
                    }
                }
            }

            // Track estimated presence (Inside)
            if (!isLatestDenied || isLatestBypass) {
                if (!uniquePatronPresenceMap.has(patronId)) {
                    uniquePatronPresenceMap.set(patronId, {
                        hasCheckedOut: !!log.checkedOutAt
                    });
                }
            }

            // Process every scan event in telemetry scans array
            const scanEvents = (log.scans && Array.isArray(log.scans) && log.scans.length > 0)
                ? log.scans
                : [{
                    timestamp: log.lastScannedAt || log.scannedAt,
                    method: log.method || 'Check-in',
                    type: log.entryType || 'Member',
                    status: log.status,
                    isBypassed: log.status === 'bypassed',
                    isExpired: log.isExpired,
                    isFlagged: log.isFlagged,
                    hasIncident: log.hasIncident,
                    isBanned: log.isBanned
                }];

            scanEvents.forEach(scan => {
                stats.totalScans++;

                const scanDate = scan.timestamp?.toDate ? scan.timestamp.toDate() : (scan.timestamp ? new Date(scan.timestamp) : new Date());
                const monthYear = scanDate.toLocaleString('default', { month: 'short', year: '2-digit' });
                const hour = scanDate.getHours();
                const dayName = days[scanDate.getDay()];
                dayOfWeekMap[dayName]++;

                const isBypass = scan.isBypassed || scan.status === 'bypassed';
                const isDenied = scan.isExpired || scan.isFlagged || scan.hasIncident || scan.isBanned || scan.status === 'denied' || scan.status === 'blocked';
                const scanType = scan.type || log.entryType || 'Member';
                const methodStr = (scan.method || '').toLowerCase();

                // Method telemetry analysis
                if (methodStr.includes('qr')) methodCounts.qr++;
                else if (methodStr.includes('self')) methodCounts.self++;
                else if (methodStr.includes('list')) methodCounts.eventList++;
                else if (methodStr.includes('quick')) methodCounts.quickAdd++;
                else methodCounts.manual++;

                if (isBypass) {
                    stats.bypassed++;
                    if (scanType === 'Guest') stats.bypassGuests++;
                    else stats.bypassMembers++;
                } else if (isDenied) {
                    stats.denied++;
                    if (scan.isExpired) stats.deniedExpired++;
                    if (scan.isFlagged) stats.deniedFlagged++;
                    if (scan.hasIncident) stats.deniedIncident++;
                    if (scan.isBanned) stats.deniedBanned++;
                }

                if (!monthlyMap[monthYear]) {
                    monthlyMap[monthYear] = { name: monthYear, Members: 0, Guests: 0, Denied: 0, Bypass: 0, total: 0 };
                }

                if (isBypass) monthlyMap[monthYear].Bypass++;
                else if (isDenied) monthlyMap[monthYear].Denied++;
                else if (scanType === 'Guest') monthlyMap[monthYear].Guests++;
                else monthlyMap[monthYear].Members++;

                monthlyMap[monthYear].total = (monthlyMap[monthYear].Members || 0) + (monthlyMap[monthYear].Guests || 0) + (monthlyMap[monthYear].Denied || 0) + (monthlyMap[monthYear].Bypass || 0);

                if (!isDenied || isBypass) {
                    if (isBypass) hourlyMap[hour].Bypassed++;
                    else if (scanType === 'Guest') hourlyMap[hour].Guests++;
                    else hourlyMap[hour].Members++;
                }
            });
        });

        stats.members = uniqueMemberIds.size;
        stats.guests = uniqueGuestIds.size;
        stats.unverifiedMembers = uniqueUnverifiedMemberIds.size;
        stats.unverifiedGuests = uniqueUnverifiedGuestIds.size;
        stats.totalUniquePatrons = uniquePatronPresenceMap.size;

        stats.reentryRate = stats.totalUniquePatrons > 0 ? (stats.totalScans / stats.totalUniquePatrons).toFixed(2) : "0.00";

        let maxHourVol = 0;
        let maxHourLabel = '--';
        hourlyMap.forEach(h => {
            const vol = h.Members + h.Guests + h.Bypassed;
            if (vol > maxHourVol) {
                maxHourVol = vol;
                maxHourLabel = h.label;
            }
        });
        stats.peakHourLabel = maxHourLabel;
        stats.peakHourVolume = maxHourVol;

        uniquePatronPresenceMap.forEach((status) => {
            if (status.hasCheckedOut) {
                stats.totalCheckedOut++;
            } else {
                stats.estimatedInClub++;
            }
        });

        const topSponsors = Object.entries(sponsorLeaderboardMap)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

        const dayOfWeekData = days.map(day => ({ name: day, Volume: dayOfWeekMap[day] }));

        const methodPieData = [
            { name: 'QR Scan', value: methodCounts.qr },
            { name: 'Self Check-in', value: methodCounts.self },
            { name: 'Master List', value: methodCounts.eventList },
            { name: 'Manual / Quick', value: methodCounts.manual + methodCounts.quickAdd }
        ].filter(d => d.value > 0);

        const bypassPieData = [
            { name: 'Member Bypass', value: stats.bypassMembers },
            { name: 'Guest Bypass', value: stats.bypassGuests }
        ].filter(d => d.value > 0);

        const denialPieData = [
            { name: 'Expired', value: stats.deniedExpired },
            { name: 'Flagged', value: stats.deniedFlagged },
            { name: 'Incident', value: stats.deniedIncident },
            { name: 'Banned', value: stats.deniedBanned }
        ].filter(d => d.value > 0);

        return { 
            monthly: Object.values(monthlyMap).reverse(), 
            hourly: hourlyMap, 
            stats, 
            methodPieData, 
            bypassPieData, 
            denialPieData, 
            dayOfWeekData, 
            topSponsors 
        };
    }, [logs]);

    const customTooltipStyle = {
        contentStyle: { backgroundColor: '#111', border: '1px solid #444', borderRadius: '8px', color: '#fff', fontSize: '10px' },
        itemStyle: { color: '#fff' },
        cursor: { fill: 'rgba(255,255,255,0.05)' }
    };

    return (
        <>
            <Button onClick={handleShow} className="hoverX shadow-sm">
                <span className="circle-btn centerCircle">📊</span>
                Access
            </Button>

            <Offcanvas show={props.isOpen} onHide={handleClose} placement="end" className="bg-dark text-light" style={{ width: '100%', maxWidth: '550px', borderLeft: '1px solid #333' }}>
                <Offcanvas.Header closeButton closeVariant="white" className="border-bottom border-secondary">
                    <Offcanvas.Title>
                        <h3 className="modified xTitle animated rollIn">Access Reportz</h3>
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-3">
                    <style>{`
                        .report-input { background: rgba(255,255,255,0.05) !important; border: 1px solid #444 !important; color: white !important; font-size: 0.75rem; padding: 5px; }
                        .pulse-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 8px 4px; text-align: center; height: 100%; min-height: 60px; display: flex; flex-direction: column; justify-content: center; }
                        .capacity-card { background: linear-gradient(135deg, rgba(64, 157, 160, 0.1), rgba(13, 202, 240, 0.05)); border: 1px solid #409da0aa; border-radius: 12px; padding: 10px; margin-bottom: 15px; }
                        .chart-container { background: rgba(255,255,255,0.02); border: 1px solid #333; border-radius: 12px; padding: 12px; margin-bottom: 15px; }
                        .stat-label { font-size: 0.5rem; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; line-height: 1.2; }
                        .stat-val { font-size: 1rem; font-weight: bold; display: block; margin-top: 2px; }
                        .today-btn { font-size: 0.6rem; padding: 4px 10px; background: rgba(64, 157, 160, 0.2); border: 1px solid #409da0cb; color: #409da0cb; border-radius: 4px; }
                        @media (max-width: 400px) {
                            .stat-val { font-size: 0.9rem; }
                            .stat-label { font-size: 0.45rem; }
                        }
                    `}</style>

                    <div className="chart-container">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="stat-label m-0"><FaCalendarAlt className="me-1" /> Range</label>
                            <button className="today-btn" onClick={() => { setStartDate(todayStr); setEndDate(todayStr); }}>TODAY</button>
                        </div>
                        <Row className="g-2">
                            <Col xs={6}><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="report-input" /></Col>
                            <Col xs={6}><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="report-input" /></Col>
                        </Row>
                    </div>

                    {/* LIVE OCCUPANCY & SHIFT EFFICIENCY SECTION */}
                    <div className="capacity-card">
                        <Row className="align-items-center text-center g-2">
                            <Col xs={4} className="border-end border-secondary border-opacity-25">
                                <span className="stat-label text-info"><FaUsers className="me-1" /> Estimated Inside</span>
                                <span className="stat-val text-info" style={{ fontSize: '1.4rem', lineHeight: '1.2' }}>
                                    {reportData.stats.estimatedInClub}
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.48rem', display: 'block', marginTop: '2px' }}>Unique Headcount</small>
                            </Col>
                            <Col xs={4} className="border-end border-secondary border-opacity-25">
                                <span className="stat-label text-warning"><FaExchangeAlt className="me-1" /> Door Velocity</span>
                                <span className="stat-val text-warning" style={{ fontSize: '1.2rem', lineHeight: '1.2' }}>
                                    {reportData.stats.reentryRate}x
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.48rem', display: 'block', marginTop: '2px' }}>Scans / Patron</small>
                            </Col>
                            <Col xs={4}>
                                <span className="stat-label text-muted"><FaDoorOpen className="me-1" /> Checked-Out</span>
                                <span className="stat-val text-muted" style={{ fontSize: '1.2rem', lineHeight: '1.2' }}>
                                    {reportData.stats.totalCheckedOut}
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.48rem', display: 'block', marginTop: '2px' }}>Geofence Left</small>
                            </Col>
                        </Row>
                    </div>

                    {/* STATS GRID */}
                    <div className="row g-2 mb-3">
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-info"><span className="stat-label">Members</span><span className="stat-val text-info">{reportData.stats.members}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-warning"><span className="stat-label">Guests</span><span className="stat-val text-warning">{reportData.stats.guests}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-primary"><span className="stat-label">Bypass</span><span className="stat-val text-primary">{reportData.stats.bypassed}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-danger"><span className="stat-label">Denied</span><span className="stat-val text-danger">{reportData.stats.denied}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-secondary"><span className="stat-label">Unverified</span><span className="stat-val text-warning" style={{ fontSize: '0.82rem' }}>{reportData.stats.unverifiedMembers}M / {reportData.stats.unverifiedGuests}G</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-secondary"><span className="stat-label">Total Scans</span><span className="stat-val text-white">{reportData.stats.totalScans}</span></div>
                        </div>
                    </div>

                    {/* VISUAL CHARTS ROW: METHOD BREAKDOWN & DENIALS */}
                    <Row className="g-2 mb-3">
                        <Col xs={12} sm={6}>
                            <div className="chart-container h-100">
                                <h6 className="stat-label mb-2 text-center"><FaQrcode className="me-1" /> Scan Method</h6>
                                <ResponsiveContainer width="100%" height={120}>
                                    <PieChart>
                                        <Pie data={reportData.methodPieData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value">
                                            {reportData.methodPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...customTooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="text-center" style={{ fontSize: '0.52rem', opacity: 0.8 }}>
                                    QR Code | Self Check-in | Master List
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="chart-container h-100">
                                <h6 className="stat-label mb-2 text-center"><FaBan className="me-1" /> Denial Reasons</h6>
                                <ResponsiveContainer width="100%" height={120}>
                                    <PieChart>
                                        <Pie data={reportData.denialPieData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value">
                                            {reportData.denialPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[(index + 1) % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...customTooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="text-center" style={{ fontSize: '0.52rem', display: 'flex', justifyContent: 'space-between', padding: '0 5px' }}>
                                    <span style={{ color: COLORS[1] }}>EXP</span><span style={{ color: COLORS[2] }}>FLG</span><span style={{ color: COLORS[3] }}>INC</span><span style={{ color: COLORS[0] }}>BAN</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* DAY OF WEEK DISTRIBUTION CHART */}
                    <div className="chart-container">
                        <h6 className="stat-label mb-3 text-center">Day-of-Week Door Volume</h6>
                        <ResponsiveContainer width="100%" height={150}>
                            <BarChart data={reportData.dayOfWeekData} margin={{ top: 10, right: 5, left: -35, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                                <XAxis dataKey="name" stroke="#555" fontSize={8} />
                                <YAxis stroke="#555" fontSize={8} />
                                <Tooltip {...customTooltipStyle} />
                                <Bar dataKey="Volume" fill="#0dcaf0cb" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* TOP HOST / SPONSOR LEADERBOARD */}
                    {reportData.topSponsors.length > 0 && (
                        <div className="chart-container">
                            <h6 className="stat-label mb-2 text-center"><FaAward className="me-1 text-warning" /> Top Guest Sponsors / Hosts</h6>
                            <div className="p-1">
                                {reportData.topSponsors.map((sponsor, idx) => (
                                    <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom border-secondary border-opacity-25" style={{ fontSize: '0.75rem' }}>
                                        <span className="text-white fw-bold">{idx + 1}. {sponsor.name}</span>
                                        <span className="badge bg-warning text-dark">{sponsor.count} {sponsor.count === 1 ? 'Guest' : 'Guests'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="chart-container">
                        <h6 className="stat-label mb-3 text-center">Monthly Distribution</h6>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={reportData.monthly} margin={{ top: 10, right: 5, left: -35, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                                <XAxis dataKey="name" stroke="#555" fontSize={8} />
                                <YAxis stroke="#555" fontSize={8} />
                                <Tooltip {...customTooltipStyle} />
                                <Bar dataKey="Members" stackId="a" fill={COLORS[0]} />
                                <Bar dataKey="Guests" stackId="a" fill={COLORS[1]} />
                                <Bar dataKey="Bypass" stackId="a" fill={COLORS[2]} />
                                <Bar dataKey="Denied" stackId="a" fill={COLORS[3]} radius={[4, 4, 0, 0]}>
                                    <LabelList dataKey="total" position="top" style={{ fill: '#888', fontSize: '9px' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="chart-container">
                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                            <h6 className="stat-label m-0"><FaClock className="me-1" /> Peak Times</h6>
                            <span className="stat-label text-info" style={{ fontSize: '0.55rem' }}>
                                Peak: <strong>{reportData.stats.peakHourLabel}</strong> ({reportData.stats.peakHourVolume} scans)
                            </span>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={reportData.hourly} margin={{ top: 10, right: 5, left: -35, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                                <XAxis dataKey="label" stroke="#555" fontSize={7} interval={4} />
                                <YAxis stroke="#555" fontSize={7} />
                                <Tooltip {...customTooltipStyle} />
                                <Area type="monotone" dataKey="Members" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.2} strokeWidth={2} />
                                <Area type="monotone" dataKey="Guests" stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.1} strokeWidth={2} />
                                <Area type="monotone" dataKey="Bypassed" stroke={COLORS[2]} fill={COLORS[2]} fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 4" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default AccessReports;
